/** TARA workspace scoring: Critical Thinking + Problem Solving raw → 0–100 score. Writing Task is submitted-only. */

import {
  clearPaperScores,
  loadPaperScores,
  readUserCache,
  savePaperScore,
  writeUserCache,
} from './paperProgressCache';

export const TARA_SCORES_KEY = 'tara_paper_scores';
export const TARA_TARGET_KEY = 'tara_workspace_target';

export function taraScoreFromRaw(correct, total) {
  const t = Number(total);
  if (!t || t <= 0) return null;
  const c = Math.max(0, Number(correct) || 0);
  return Math.round((c / t) * 100 * 10) / 10;
}

export function formatTaraScore(score) {
  if (score == null || !Number.isFinite(Number(score))) return '—';
  return `${Number(score).toFixed(1)}/100`;
}

export function formatPaperRaw(correct, total) {
  if (total == null || total <= 0 || correct == null) return '—';
  return `${correct}/${total}`;
}

export function loadTaraPaperScores(userId) {
  return loadPaperScores(TARA_SCORES_KEY, userId);
}

export function saveTaraPaperScore(userId, paperId, score = {}) {
  savePaperScore(TARA_SCORES_KEY, userId, paperId, score);
}

export function clearTaraPaperScores(userId, paperIds = []) {
  clearPaperScores(TARA_SCORES_KEY, userId, paperIds);
}

/** Forget the user's cached scores for a sitting's papers (a retake). */
export function resetTaraSittingProgress(userId, paperIds = []) {
  clearTaraPaperScores(userId, paperIds);
}

export function loadTaraTarget(userId) {
  const fallback = {
    value: 64,
    label: 'London School of Economics (LSE) — BSc Politics & Economics',
    custom: false,
  };
  return readUserCache(TARA_TARGET_KEY, userId, fallback);
}

export function saveTaraTarget(userId, target) {
  writeUserCache(TARA_TARGET_KEY, userId, target);
}

/**
 * Combine Critical Thinking + Problem Solving into one sitting score.
 * moduleIds: { critical_thinking, problem_solving, writing } — paper ids for each module.
 * Score is a combined percentage over pooled raw totals (not a mean of two percentages),
 * because CT and PS have different question counts.
 */
function taraModulePaperId(value) {
  if (!value) return null;
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return value.id || value.catalog_id || null;
}

export function sittingScoreFromTaraModules(moduleIds, scoresMap = {}) {
  const ctId = taraModulePaperId(moduleIds?.critical_thinking);
  const psId = taraModulePaperId(moduleIds?.problem_solving);
  const writingId = taraModulePaperId(moduleIds?.writing);

  const ct = ctId ? scoresMap[String(ctId)] : null;
  const ps = psId ? scoresMap[String(psId)] : null;
  const writing = writingId ? scoresMap[String(writingId)] : null;

  const ctDone = Boolean(ct && (ct.total > 0 || ct.dbStatus === 'completed'));
  const psDone = Boolean(ps && (ps.total > 0 || ps.dbStatus === 'completed'));
  const writingDone = Boolean(writing && (writing.dbStatus === 'completed' || writing.total > 0));

  let score = null;
  if (ctDone && psDone) {
    score = taraScoreFromRaw(
      (ct.correct || 0) + (ps.correct || 0),
      (ct.total || 0) + (ps.total || 0)
    );
  }

  const mode = ct?.mode || ps?.mode || writing?.mode || null;
  const anyDone = ctDone || psDone || writingDone;
  const allDone = ctDone && psDone && writingDone;
  const status = allDone ? 'done' : anyDone ? 'progress' : 'none';

  return {
    ct: ctDone ? ct : null,
    ps: psDone ? ps : null,
    writing: writingDone ? writing : null,
    score,
    mode,
    status,
  };
}

/** N-paper (3-module) status resolver — TMUA's sittingStatusFromDbPapers is 2-paper specific. */
export function taraStatusFromDbPapers(paperIds = [], dbPapers = {}) {
  const statuses = paperIds
    .filter(Boolean)
    .map((id) => dbPapers[String(id)]?.status)
    .filter(Boolean);
  if (!statuses.length) return 'none';
  if (statuses.every((s) => s === 'completed')) return 'done';
  if (statuses.some((s) => s === 'in_progress' || s === 'completed')) return 'progress';
  return 'none';
}

/**
 * Hub row status for a TARA sitting. Writing is one essay from three prompts,
 * so an in_progress writing paper must not keep the sitting unfinished when
 * that essay exists (feedback availability) and CT/PS are done.
 */
export function taraSittingHubStatus({
  paperIds = [],
  dbPapers = {},
  scored = {},
  writingPaperId,
  writingAvailability = {},
} = {}) {
  let status = taraStatusFromDbPapers(paperIds, dbPapers);
  if (status === 'none') {
    status = scored.status || 'none';
  }
  if (status === 'done') return 'done';

  const writingId = writingPaperId ? String(writingPaperId) : '';
  const writingSubmitted = Boolean(
    writingId && (
      writingAvailability[writingId]?.attempt_id
      || dbPapers[writingId]?.status === 'completed'
      || scored.writing
    )
  );
  if (scored.ct && scored.ps && writingSubmitted) return 'done';
  const otherIds = paperIds.filter((id) => id && String(id) !== writingId);
  const othersStarted = otherIds.length >= 2 && otherIds.every((id) => {
    const s = dbPapers[String(id)]?.status;
    return s && s !== 'not_started';
  });
  if (writingSubmitted && othersStarted) return 'done';
  return status;
}
